import { useEffect } from 'react';
import type { FC } from 'react';

import type { FormikProps } from 'formik';
import { withFormik } from 'formik';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'recompact';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { IntentButton, TextContainer } from 'src/app/components/Pages/GetStarted/OnboardingSteps/IntentStep/styles';
import type { ProgressStep } from 'src/app/components/Pages/GetStarted/StepLayout/consts';
import {
  MEDICATION_PROGRESS_STEP,
  PROVIDER_DOCTOR_PROGRESS_STEP,
  STEP_KEY,
} from 'src/app/components/Pages/GetStarted/StepLayout/consts';
import { Step } from 'src/app/components/Pages/GetStarted/StepLayout/Step';
import { segmentTrack } from 'src/app/state/actions';
import {
  addOnboardingStepAfterGivenStepKey,
  removeOnboardingStep,
  setOnboardingFlow,
  setOnboardingFormValues,
} from 'src/app/state/actions/onBoardingFlow';
import { isLoggedIn } from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';

import { FLOW } from '../../consts';

const mapStateToProps = (state: RootState) => ({
  onboardingform: state.onboardingForm,
  isLoggedIn: isLoggedIn(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    segmentTrack: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
    onSetOnboardingFlow: (flow: FLOW) => dispatch(setOnboardingFlow(flow)),
    onAddStepAfterKey: (stepKey: STEP_KEY, progressStep: ProgressStep) =>
      dispatch(addOnboardingStepAfterGivenStepKey(stepKey, progressStep)),
    onSetOnboardingFormValues: values => dispatch(setOnboardingFormValues(values)),
    onResetOnboardingSteps: () => {
      dispatch(removeOnboardingStep(STEP_KEY.MEDICATIONS));
      dispatch(removeOnboardingStep(STEP_KEY.PROVIDER));
    },
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
const enhanceWithFormik = withFormik({
  mapPropsToValues: ({ onboardingForm }) => ({
    isDoctorTransfer: false,
    ...onboardingForm,
  }),
  handleSubmit: (values, { props }) => {
    const { isDoctorTransfer } = values;
    const {
      onSetOnboardingFormValues,
      handleNextClick,
      onSetOnboardingFlow,
      onAddStepAfterKey,
      onResetOnboardingSteps,
    } = props;
    if (isDoctorTransfer) {
      onSetOnboardingFlow(FLOW.TRANSFER);
      // Ensures no duplicate step adds
      onResetOnboardingSteps();
      onAddStepAfterKey(STEP_KEY.DOCTOR_INTENT, PROVIDER_DOCTOR_PROGRESS_STEP);
      onAddStepAfterKey(STEP_KEY.PROVIDER, MEDICATION_PROGRESS_STEP);
    } else {
      onSetOnboardingFlow(FLOW.DOCTOR_ESCRIPT);
      onResetOnboardingSteps();
    }

    onSetOnboardingFormValues(values);
    handleNextClick();
  },
});

type PropsFromRedux = ConnectedProps<typeof connector>;

interface FormValues {
  isDoctorTransfer: boolean;
  isNoRxTransferFlow: boolean;
}

const UnconnectedDoctorIntentStep: FC<PropsFromRedux & FormikProps<FormValues>> = ({
  handleSubmit,
  setFieldValue,
  segmentTrack,
}) => {
  useEffect(() => {
    segmentTrack(UNIFIED.viewTransferForm, { location: 'Onboarding Transfer Form' });
    segmentTrack(UNIFIED.viewOnboardingRxFromDoctor, {});
  }, [segmentTrack]);

  const setIsDoctorTransferFlowAndSubmit = (isDoctorTransfer: boolean) => () => {
    if (isDoctorTransfer) {
      segmentTrack(UNIFIED.selectOnboardingAddDoctorInfo, {});
    } else {
      segmentTrack(UNIFIED.selectDoctorSentRx, {});
    }
    setOnboardingFormValues({ isDoctorTransfer });
    setFieldValue('isDoctorTransfer', isDoctorTransfer);
    setOnboardingFormValues({ isNoRxTransferFlow: !isDoctorTransfer });
    setFieldValue('isNoRxTransferFlow', !isDoctorTransfer);
    handleSubmit();
  };

  return (
    <Step
      title={`Has your doctor already sent a prescription to Capsule?`}
      text={''}
      customHandlePrevText={'Back'}
      hideCount
    >
      <IntentButton onClick={setIsDoctorTransferFlowAndSubmit(false)}>
        <TextContainer>Yes</TextContainer>
      </IntentButton>

      <IntentButton onClick={setIsDoctorTransferFlowAndSubmit(true)}>
        <TextContainer>No</TextContainer>
      </IntentButton>
    </Step>
  );
};

export const DoctorIntentStep = compose(connector, enhanceWithFormik)(UnconnectedDoctorIntentStep);
